import { useSystemsList } from '@/api/hooks/useCrossroads'
import {
  type TankReadingConfig,
  useTenantConfig,
  useUpsertTankReadingConfig,
} from '@/api/hooks/useTenantConfig'
import { BasicSelectEditor } from '@/components/Grid/CellEditors/BasicSelectEditor'
import { suppressDropdownKeyboardEvent } from '@/components/Grid/CellEditors/MRDMultiSelectEditor'
import { HeaderWithTooltip } from '@/components/Grid/HeaderWithTooltip'
import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import type {
  ColDef,
  ICellEditorParams,
  ICellRendererParams,
} from 'ag-grid-community'
import { Drawer, Modal, Tag, Typography, message, theme } from 'antd'
import { useState } from 'react'
import { LearnMoreAlert } from './LearnMoreAlert'
import { ShareReadingsCell } from './ShareReadingsCell'
import { isSendLocked, sdInstances } from './connectedSystem'
import { deriveShareReadingState } from './shareReadings'

const SHARING_TOOLTIP =
  "Control tank-reading sharing with this partner. Toggle 'Send' to forward your tank readings to them. If they have already opted to send you readings, you can toggle 'Receive' to receive them."

const INSTANCES_TOOLTIP =
  'Which of your Supply & Dispatch instances this partner’s readings are forwarded to.'

export const TankReadingConfigsGrid = () => {
  const { token } = theme.useToken()
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false)
  const { data: config, isLoading, refetch } = useTenantConfig()
  const { data: externalSystems } = useSystemsList()
  const upsertMutation = useUpsertTankReadingConfig()

  const configs = config?.tank_reading_configs || []
  const instances = config?.instances || []
  const receivable = sdInstances(instances, externalSystems)
  const instanceOptions = receivable.map((i) => ({
    label: i.name,
    value: i.id,
  }))

  const handleShareToggle = (row: TankReadingConfig, next: boolean) => {
    const { mode } = deriveShareReadingState(row)
    const isReceive = mode === 'receive'
    const verb = isReceive ? 'Receive Readings' : 'Send Readings'
    const name = row.tenant_name
    Modal.confirm({
      title: `${next ? 'Turn on' : 'Turn off'} ${verb}?`,
      okText: next ? 'Yes, turn on' : 'Yes, turn off',
      okButtonProps: next ? undefined : { danger: true },
      content: isReceive
        ? next
          ? `Start receiving ${name}'s tank readings? Use Receiving Instances to change where Crossroads sends them..`
          : `Stop receiving ${name}'s tank readings? New readings will stop flowing to your Tank Readings page, S&D, and the API.`
        : next
          ? `Share your tank readings with ${name}?`
          : `Stop sharing your tank readings with ${name}? ${name} will no longer receive your readings.`,
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          upsertMutation.mutate(
            {
              partner_tenant_id: row.tenant_id,
              ...(isReceive
                ? { receive_readings: next }
                : { send_readings: next }),
            },
            {
              onSuccess: () => {
                message.success('Reading sharing updated')
                refetch()
                resolve()
              },
              onError: (error) => {
                message.error(`Failed to update sharing: ${error.message}`)
                reject(error)
              },
            },
          )
        }),
    })
  }

  const columnDefs: ColDef<TankReadingConfig>[] = [
    {
      colId: 'tenant_name',
      field: 'tenant_name',
      headerName: 'Partner',
      editable: false,
      flex: 1,
      minWidth: 180,
    },
    {
      colId: 'share_readings',
      headerName: 'Sharing',
      width: 170,
      initialFlex: 0,
      minWidth: 170,
      editable: false,
      sortable: false,
      filter: false,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltip: SHARING_TOOLTIP },
      cellRenderer: (params: ICellRendererParams<TankReadingConfig>) => {
        const row = params.data
        if (!row) return null
        return (
          <ShareReadingsCell
            config={row}
            onToggle={handleShareToggle}
            sendDisabled={isSendLocked(instances, externalSystems)}
          />
        )
      },
    },
    {
      colId: 'receiving_instances',
      headerName: 'Receiving Instances',
      flex: 1,
      minWidth: 220,
      sortable: false,
      filter: false,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltip: INSTANCES_TOOLTIP },
      // Only meaningful once the partner's feed has been accepted.
      editable: ({ data }) => !!data?.receive_readings,
      cellEditor: BasicSelectEditor,
      cellEditorPopup: true,
      cellEditorParams: (params: ICellEditorParams<TankReadingConfig>) => ({
        mode: 'multiple',
        options: instanceOptions,
        // The popup editor sizes to its content, so pin it to the cell it covers.
        style: { width: params.column.getActualWidth() },
      }),
      suppressKeyboardEvent: suppressDropdownKeyboardEvent,
      valueGetter: ({ data }) =>
        (data?.receiving_instances || []).map((i) => i.instance_id),
      valueSetter: ({ data, newValue }) => {
        const ids: string[] = newValue || []
        data.receiving_instances = ids.map((id) => ({
          instance_id: id,
          instance_name: receivable.find((i) => i.id === id)?.name || '',
        }))
        return true
      },
      cellRenderer: (params: ICellRendererParams<TankReadingConfig>) => {
        const row = params.data
        if (!row?.receive_readings) return <span>—</span>
        if (!row.receiving_instances.length) {
          return (
            <Typography.Text style={{ color: token.colorWarning }}>
              No instances — nothing is forwarded
            </Typography.Text>
          )
        }
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {row.receiving_instances.map((i) => (
              <Tag key={i.instance_id}>{i.instance_name}</Tag>
            ))}
          </div>
        )
      },
    },
  ]

  return (
    <Vertical bg='layout' style={{ height: '100%', width: '100%' }}>
      <LearnMoreAlert
        message='Configure sending/receiving tank readings.'
        onLearnMore={() => setIsLearnMoreOpen(true)}
      />
      <GraviGrid
        updateEP={async (edited) => {
          // GraviGrid hands back a single row or a bulk selection; the column only edits one.
          const rows = Array.isArray(edited) ? edited : [edited]
          for (const row of rows) {
            upsertMutation.mutate(
              {
                partner_tenant_id: row.tenant_id,
                receiving_instance_ids: row.receiving_instances.map(
                  (i) => i.instance_id,
                ),
              },
              {
                onSuccess: () => {
                  message.success('Receiving instances updated')
                  refetch()
                },
                onError: (error) => {
                  message.error(`Failed to save instances: ${error.message}`)
                  // Put the grid back on what the server actually has.
                  refetch()
                },
              },
            )
          }
        }}
        rowData={configs}
        columnDefs={columnDefs}
        loading={isLoading}
        storageKey='tenant-tank-reading-configs-v2'
        controlBarProps={{ title: 'Tank Reading Sharing' }}
        agPropOverrides={{
          getRowId: (params) => params.data.tenant_id,
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
          },
          animateRows: true,
        }}
      />
      <Drawer
        title='About Tank Reading Sharing'
        open={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
      >
        <Typography.Paragraph>
          Sending is configured per-partner. Your partners choose where to route
          received readings.
        </Typography.Paragraph>
        <br />
        <Typography.Paragraph>
          If a partner is sending you readings, the toggle will allow you to
          receive readings and choose where they are routed.
        </Typography.Paragraph>
      </Drawer>
    </Vertical>
  )
}
